import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { HOME_ROUTE } from '../config/routes'

const pageStyles: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
}

const containerStyles: React.CSSProperties = {
  width: '100%',
  maxWidth: '400px',
}

const cardStyles: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '0.75rem',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  padding: '2rem',
}

const headerStyles: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '2rem',
}

const titleStyles: React.CSSProperties = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#111827',
  marginBottom: '0.5rem',
}

const subtitleStyles: React.CSSProperties = {
  fontSize: '1rem',
  color: '#6b7280',
}

const formStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
}

const inputGroupStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
}

const labelStyles: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
}

const inputStyles: React.CSSProperties = {
  padding: '0.75rem',
  borderRadius: '0.5rem',
  border: '1px solid #d1d5db',
  fontSize: '1rem',
  transition: 'border-color 0.2s',
}

const buttonStyles: React.CSSProperties = {
  backgroundColor: '#16a34a',
  color: 'white',
  padding: '0.75rem 1rem',
  borderRadius: '0.5rem',
  border: 'none',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
}

const errorStyles: React.CSSProperties = {
  color: '#dc2626',
  fontSize: '0.875rem',
  marginTop: '0.5rem',
}

const linkStyles: React.CSSProperties = {
  textAlign: 'center',
  marginTop: '1rem',
  fontSize: '0.875rem',
  color: '#6b7280',
}

const linkAnchorStyles: React.CSSProperties = {
  color: '#2563eb',
  textDecoration: 'none',
  fontWeight: '500',
}

interface SignupForm {
  email: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean
}

const SignupPage: React.FC = () => {
  const [form, setForm] = useState<SignupForm>({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    agreeToTerms: false
  })
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { signup, isAuthenticated, isLoading: authLoading } = useAuth()
  const justSignedUpRef = React.useRef(false)

  useEffect(() => {
    // Skip the auto-redirect when we just signed up via this page;
    // handleSubmit navigates once the account exists.
    if (justSignedUpRef.current) return
    if (!authLoading && isAuthenticated) {
      navigate(HOME_ROUTE, { replace: true })
    }
  }, [isAuthenticated, authLoading, navigate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setForm(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }))
    setError('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    console.log('📝 SignupPage handleSubmit started')
    console.log('📝 Form data:', { ...form, password: '[HIDDEN]', confirmPassword: '[HIDDEN]' })
    setIsLoading(true)
    setError('')

    // Basic validation
    if (form.password !== form.confirmPassword) {
      console.log('❌ Password validation failed: passwords do not match')
      setError('Passwords do not match')
      setIsLoading(false)
      return
    }

    if (form.password.length < 8) {
      console.log('❌ Password validation failed: too short')
      setError('Password must be at least 8 characters long')
      setIsLoading(false)
      return
    }

    if (!form.agreeToTerms) {
      console.log('❌ Terms validation failed: not agreed')
      setError('利用規約に同意する必要があります')
      setIsLoading(false)
      return
    }

    console.log('✅ Form validation passed, calling signup...')
    try {
      await signup(form.email, form.password)
      justSignedUpRef.current = true
      console.log('✅ Signup successful, preparing to navigate...')

      // New tenants land in the chat, which walks them through setup in
      // conversation (company → product → sender) and can answer questions a
      // form cannot. The wizard is still there for anyone who wants it.
      const from = (location.state as any)?.from?.pathname || HOME_ROUTE
      console.log('🚀 Navigating to:', from)
      navigate(from, { replace: true })
    } catch (err) {
      console.error('❌ Signup failed in SignupPage:', err)
      setError(err instanceof Error ? err.message : 'Signup failed. Please try again.')
    } finally {
      setIsLoading(false)
      console.log('📝 SignupPage handleSubmit completed')
    }
  }

  return (
    <div style={pageStyles}>
      <div style={containerStyles}>
        <div style={cardStyles}>
          <div style={headerStyles}>
            <h1 style={titleStyles}>Join Spread</h1>
            <p style={subtitleStyles}>Create your account</p>
          </div>
          
          <form style={formStyles} onSubmit={handleSubmit}>

            <div style={inputGroupStyles}>
              <label htmlFor="email" style={labelStyles}>
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleInputChange}
                style={inputStyles}
                placeholder="Enter your email"
                autoComplete="username"
                inputMode="email"
                required
              />
            </div>

            <div style={inputGroupStyles}>
              <label htmlFor="password" style={labelStyles}>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleInputChange}
                style={inputStyles}
                placeholder="8文字以上"
                autoComplete="new-password"
                aria-describedby="password-hint"
                required
                minLength={8}
              />
              <p id="password-hint" style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                8文字以上の英数字を推奨
              </p>
            </div>

            <div style={inputGroupStyles}>
              <label htmlFor="confirmPassword" style={labelStyles}>
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={form.confirmPassword}
                onChange={handleInputChange}
                style={inputStyles}
                placeholder="Confirm your password"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={form.agreeToTerms}
                onChange={handleInputChange}
                style={{ width: '1rem', height: '1rem' }}
                required
              />
              <label htmlFor="agreeToTerms" style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                <Link to="/terms-of-service" target="_blank" style={{ color: '#2563eb' }}>
                  利用規約
                </Link>
                に同意します
              </label>
            </div>
            
            {error && (
              <div role="alert" aria-live="assertive" style={errorStyles}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...buttonStyles,
                ...(isLoading ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
              }}
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <div style={linkStyles}>
            Already have an account?{' '}
            <Link to="/login" style={linkAnchorStyles}>
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupPage